import Entity from "_framework/entities/entity";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import DocumentSchema, { DocumentJSON, DocumentParams } from "documents/_schema/document-schema";
import DocumentName from "documents/names/document-name";
import DocumentTopic from "documents/topics/document-topic";
import { DocumentClassificationStatus, hasNoExtractableText, isBeingProcessed, isProcessed, needsExplaining, processingFailed } from "documents/classification/document-classification-status";
import { DocumentClassificationSource } from "documents/classification/document-classification-source";
import { DocumentFileType } from "documents/document-file-type";
import { DocumentContentAccess } from "documents/classification/document-content-access";
import { DocumentFileScanStatus } from "documents/downloads/document-file-scan-status";
import { DocumentSubjectMatter } from "permissions/resources/documents/document-subject-matter";
import { JSONType } from "work/proposals/redlining/field-redlines/field-redline";

export default class Document extends Entity<Document, DocumentJSON, DocumentParams> {
  public static create(values: DocumentParams): Document {
    const parsed = DocumentSchema.validate(values);
    return new Document(parsed);
  }

  public static fromJSON(json: unknown): Document {
    if (!DocumentSchema.isValidJSON(json)) {
      throw new InvalidDocumentJSONError(json as JSONType, "JSON does not conform to DocumentSchema.");
    }
    const parsed = DocumentSchema.deserialize(json);
    return new Document(parsed);
  }

  public static fromJSONArray(jsonArray: DocumentJSON[]): Document[] {
    return jsonArray.map((json) => Document.fromJSON(json));
  }

  public readonly id: Guid;
  public readonly creatorId?: Guid; // TODO: Convert to EntityMember type (needs to happen on the backend first)
  public readonly created?: AHDate;
  public readonly name: DocumentName = new DocumentName("Untitled Document");
  public readonly fileSize?: number; // TODO: Move into File type
  public readonly fileType: DocumentFileType; // TODO: Move into File type
  public readonly readAccess?: Guid[];
  public readonly writeAccess?: Guid[];
  public readonly hasSystemPermissions: boolean = false;
  public readonly replacesId?: Guid;
  public readonly isArchived?: boolean;
  public readonly inUse: boolean = false;
  /**
   * How far this document has got through classification, or undefined if the payload did not
   * say. Optional rather than defaulted, because "not stated" and "still processing" are
   * different claims and only one of them is safe to make up.
   */
  public readonly classificationStatus?: DocumentClassificationStatus;
  /**
   * The subject-matter labels that decide who may read this document's content, or undefined
   * when the payload did not carry them. The API sends them only to a reader who may see the
   * content, and only an empty list means "classified, and carries no label".
   */
  public readonly subjectMatters?: DocumentSubjectMatter[];
  /** The content version the labels describe. A label override sends it back. */
  public readonly classificationContentHash?: string;
  public readonly classificationSource?: DocumentClassificationSource;
  public readonly classificationOverriddenByUserId?: Guid;
  /** The display name of whoever overrode the labels, when the API could resolve one. */
  public readonly classificationOverriddenByName?: string;
  public readonly classificationOverriddenAt?: AHDate;
  /**
   * Whether the reader meets every rule of the label override. The API answers it, so the
   * control is offered only where the override would not be refused.
   */
  public readonly canOverrideClassification: boolean = false;
  /**
   * Whether this reader may read the content, as the documents API decided it, or undefined when
   * the payload did not say (#6992). See `documentDownloadWithheldReason`.
   */
  public readonly contentAccess?: DocumentContentAccess;
  /** The subject matters this reader lacks, where `contentAccess` is `SubjectMatterRestricted`. */
  public readonly restrictedSubjectMatters?: DocumentSubjectMatter[];
  /** What the malware scan of the file said, or undefined when the payload did not say. */
  public readonly scanStatus?: DocumentFileScanStatus;

  // TODO: Missing "updated: AHDate" field.

  public get topics(): DocumentTopic[] {
    return [...this._topics];
  }

  public get templateIds(): Guid[] {
    return [...this._templateIds];
  }

  public get string(): string {
    return this.toString();
  }

  /** Still being classified, so its contents cannot be answered from yet. */
  public get isBeingProcessed(): boolean {
    return isBeingProcessed(this.classificationStatus);
  }

  /** Classified, so its contents are answerable subject to the reader's content grants. */
  public get isProcessed(): boolean {
    return isProcessed(this.classificationStatus);
  }

  /**
   * Classification ran and failed. Unlike processing, this does not resolve on its own, and
   * the file's contents stay withheld while it lasts.
   */
  public get processingFailed(): boolean {
    return processingFailed(this.classificationStatus);
  }

  /**
   * The file holds no text to search - a scan, a photo. Nothing is withheld and nothing is
   * wrong; it simply has nothing in it for the assistant to read.
   */
  public get hasNoSearchableText(): boolean {
    return hasNoExtractableText(this.classificationStatus);
  }

  /**
   * Whether this document's processing state is worth showing the reader.
   *
   * A processed document is the expected case and needs no annotation; an unstated one has
   * nothing to say. Only the three states that explain why a document is not answerable do.
   */
  public get needsProcessingExplained(): boolean {
    return needsExplaining(this.classificationStatus);
  }

  /** A person corrected the labels, rather than the classifier choosing them. */
  public get labelsWereSetByHand(): boolean {
    return this.classificationSource === DocumentClassificationSource.ManualOverride;
  }

  public get isTemplate(): boolean {
    return this.topics.some((topic) => topic.context?.toLowerCase().includes("template"));
  }

  protected constructor(params: DocumentParams) {
    super(params);
    this.id = params.id;
    this.creatorId = params.creatorId;
    this.fileSize = params.fileSize;
    this.fileType = params.fileType;
    this.created = params.created;
    this.readAccess = params.readAccess;
    this.writeAccess = params.writeAccess;
    this.hasSystemPermissions = params.hasSystemPermissions ?? false;
    this.name = params.name ?? new DocumentName("Untitled Document");
    this._topics = params.topics?.map((topic) => DocumentTopic.create(topic)) ?? [];
    this._templateIds = params.templateIds ?? [];
    this.replacesId = params.replacesId;
    this.isArchived = params.isArchived;
    this.inUse = params.inUse ?? false;
    this.classificationStatus = params.classificationStatus;
    this.subjectMatters = params.subjectMatters;
    this.classificationContentHash = params.classificationContentHash;
    this.classificationSource = params.classificationSource;
    this.classificationOverriddenByUserId = params.classificationOverriddenByUserId;
    this.classificationOverriddenByName = params.classificationOverriddenByName;
    this.classificationOverriddenAt = params.classificationOverriddenAt;
    this.canOverrideClassification = params.canOverrideClassification ?? false;
    this.contentAccess = params.contentAccess;
    this.restrictedSubjectMatters = params.restrictedSubjectMatters;
    this.scanStatus = params.scanStatus;
  }

  public toJSON(): DocumentJSON {
    return DocumentSchema.serialize(this);
  }

  /**
   * This document with other topics, and everything else as it is. The same instances, the creation
   * date included, so `UpdateDocumentAPIRequest` sends the topics and nothing else.
   */
  public withTopics(topics: DocumentTopic[]): Document {
    return new Document({
      id: this.id,
      creatorId: this.creatorId,
      fileSize: this.fileSize,
      fileType: this.fileType,
      created: this.created,
      readAccess: this.readAccess,
      writeAccess: this.writeAccess,
      hasSystemPermissions: this.hasSystemPermissions,
      name: this.name,
      topics,
      templateIds: this._templateIds,
      replacesId: this.replacesId,
      isArchived: this.isArchived,
      inUse: this.inUse,
      classificationStatus: this.classificationStatus,
      subjectMatters: this.subjectMatters,
      classificationContentHash: this.classificationContentHash,
      classificationSource: this.classificationSource,
      classificationOverriddenByUserId: this.classificationOverriddenByUserId,
      classificationOverriddenByName: this.classificationOverriddenByName,
      classificationOverriddenAt: this.classificationOverriddenAt,
      canOverrideClassification: this.canOverrideClassification,
      contentAccess: this.contentAccess,
      restrictedSubjectMatters: this.restrictedSubjectMatters,
      scanStatus: this.scanStatus,
    });
  }

  public valueOf(): DocumentJSON {
    return this.toJSON();
  }

  public toString(): string {
    return `Document: ${this.name?.toString()} (ID: ${this.id.value})`;
  }

  public isEqualTo(other: Document | null | undefined): boolean {
    return Boolean(
      (!!this && !!other) &&
      this.is(other) &&
      (this.name?.isEqualTo(other.name) ?? (!this.name && !other.name)) &&
      (this.creatorId?.isEqualTo(other.creatorId) ?? (!this.creatorId && !other.creatorId)) &&
      this.fileSize === other.fileSize &&
      this.fileType === other.fileType &&
      this.topics.length === other.topics.length &&
      this.topics.every((topic) => other.topics.some((otherTopic) => topic.isEqualTo(otherTopic))) &&
      this.templateIds.length === other.templateIds.length &&
      this.templateIds.every((id) => other.templateIds.some((otherId) => id.isEqualTo(otherId))) &&
      this.readAccess?.length === other.readAccess?.length &&
      (this.readAccess?.every((id) => other.readAccess?.some((otherId) => id.isEqualTo(otherId))) ?? true) &&
      this.writeAccess?.length === other.writeAccess?.length &&
      (this.writeAccess?.every((id) => other.writeAccess?.some((otherId) => id.isEqualTo(otherId))) ?? true) &&
      this.isArchived === other.isArchived &&
      this.inUse === other.inUse &&
      this.subjectMatters?.join() === other.subjectMatters?.join() &&
      this.classificationContentHash === other.classificationContentHash &&
      this.canOverrideClassification === other.canOverrideClassification
    );
  }

  public isUsingTemplate = (templateId: Guid): boolean => this.templateIds.some((id) => id.isEqualTo(templateId));

  public is = (document: Document | null | undefined): boolean => !!document && this.id.isEqualTo(document.id);

  private readonly _topics: DocumentTopic[];
  private readonly _templateIds: Guid[] = [];
}

export class InvalidDocumentJSONError extends Error {
  constructor(public readonly json: JSONType, message?: string) {
    super("Invalid Document JSON" + (message ? `: ${message}` : "."));
  }
}
